import { Box, Card, Typography, useTheme, Tabs, Tab } from "@mui/material";
import { useGlobalConfig } from "context/GlobalContext";
import React, { useEffect, useState, useCallback } from "react";
import { ClipboardPenLine, FilePlus, LucideLibrary } from 'lucide-react';
import { SurveyQuestionBank } from "../questionBank/SurveyQuestionBank";
import { SurveyTemplatesContainer } from "../template/SurveyTemplatesContainer";
import { useDispatch, useSelector } from "react-redux";
import { SurveyQBApis } from "apis/surveyQBAPI";
import { SurveyTemplateAPI } from "apis/surveyTemplateAPI";
import { setQuestionBankAction, setReviewWorkflowAction } from "store/dataReducers/surveyQBSlice";
import { setTemplatesAction, setTemplateReviewWorkflowAction } from "store/dataReducers/surveyTemplateSlice";
import { RootState } from "store/Store";
import { CMSPilotsReducerState } from "store/dataReducers/cmsPilotSlice";
import { BglLoading } from "components/loading/BglLoading";
import { BglPrimaryButton } from "components/button/BglButtons";

export const SurveyHome = () => {

  const theme = useTheme();
  const dispatch = useDispatch();
  const { rc } = useGlobalConfig();

  const pilotState = useSelector((state : RootState) => state.cmsPilot) as CMSPilotsReducerState;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getQuestionBankAndTemplates = useCallback(async () => {

    setLoading(true);
    const [questionBankResp, qbReviewWorkflowResp, templatesResp, templateReviewWorkflowResp] = await Promise.all([
      SurveyQBApis.getQuestionBank(rc, pilotState.selectedPilotDocId),
      SurveyQBApis.getReviewWorkflow(rc),
      SurveyTemplateAPI.getTemplates(rc, pilotState.selectedPilotDocId),
      SurveyTemplateAPI.getReviewWorkflow(rc)
    ])

    if (questionBankResp.error || qbReviewWorkflowResp.error || templatesResp.error || templateReviewWorkflowResp.error) {
      setError(questionBankResp.error || qbReviewWorkflowResp.error || templatesResp.error || templateReviewWorkflowResp.error);
    } else {
      setError(null);
      dispatch(setQuestionBankAction(questionBankResp!));
      dispatch(setReviewWorkflowAction(qbReviewWorkflowResp.data!));
      dispatch(setTemplatesAction(templatesResp.data!));
      dispatch(setTemplateReviewWorkflowAction(templateReviewWorkflowResp.data!));
    }

    setLoading(false);
  }, [rc, pilotState.selectedPilotDocId, dispatch]);

  useEffect(() => {
    getQuestionBankAndTemplates();
  }, [getQuestionBankAndTemplates]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', paddingTop: '20px', 
      paddingLeft: '20px', paddingRight: '20px', height: '100%', overflowY: 'auto'}}> 

      <Card style={{ padding: '15px', marginBottom: '20px', overflow: 'unset' }}>  
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center' }}>
              <ClipboardPenLine size={24} style={{ stroke: theme.palette.primary.main, marginRight: 10 }} />
              Survey Management
            </Typography>
            <Typography variant="subtitle2" color="text.primary">
              Manage this project's survey, which is derived from the master survey templates in Admin Settings.
            </Typography>
          </Box>
        </Box>
      </Card>

      {loading ? <BglLoading /> : 
      
        error ? 
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', 
            alignItems: 'center', marginTop: '20px'}}>
          <Typography variant="h6">{error.message}</Typography>
          <BglPrimaryButton
            onClick={() => getQuestionBankAndTemplates()}>
            Retry
          </BglPrimaryButton>
        </div>
       : 
      (
        <React.Fragment>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              textColor="inherit"
              slotProps={{
                indicator: {
                  sx: { backgroundColor: theme.palette.primary.main }
                }
              }}
              sx={{
                minHeight : 'unset',
                '& .Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: '#fff !important'
                },
                '& .MuiTabs-flexContainer': {
                  justifyContent: 'space-between',
                },
                '& .MuiTabs-scroller': {
                  backgroundColor : theme.palette.divider,
                  borderRadius : '5px',
                },
                '& .MuiTab-root': {
                  flex: 1,
                  maxWidth : 'unset',
                  borderRadius : '5px',
                  color : theme.palette.text.secondary,
                  minHeight : 'unset',
                },
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
              }}
            >
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, textTransform: 'none' }}>
                    <FilePlus size={18} />
                    Survey Templates
                  </Box>
                } 
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, textTransform: 'none' }}>
                    <LucideLibrary size={18} />
                    Question Bank
                  </Box>
                } 
              />
            </Tabs>
          </Box>

          {activeTab === 0 && <SurveyTemplatesContainer />}
          {activeTab === 1 && <SurveyQuestionBank />}

        </React.Fragment>
      )}
    </div>
  )
};